/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description UploadAttachment button
 */
import { useCallback, useMemo, type ReactNode } from 'react';
import { Upload, Button, type ButtonProps } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';

import useUploadAttachmentFlow from 'hooks/claim/mutate/useUploadAttachmentFlow';
import { useUploadManagerValues } from 'store/uploadManager';
import { countEntityInFlightUploads, normalizeAcceptedExtensions, resolveUploadButtonLabel } from 'utils/uploadAttachment';

const DEFAULT_ACCEPTED_EXTENSIONS = ['pdf', 'txt'];
const DEFAULT_FILE_SIZE_LIMIT_MB = 10;

interface UploadAttachmentButtonProps extends ButtonProps {
	entityId: string;
	entityType: EntityTypeEnum;
	description?: string;
	acceptedExtensions?: string[];
	fileSizeLimitMB?: number;
	multiple?: boolean;
	idleLabel?: ReactNode;
	uploadingLabel?: ReactNode | ((count: number) => ReactNode);
}

const UploadAttachment: React.FC<UploadAttachmentButtonProps> = ({
	entityId,
	entityType,
	description,
	acceptedExtensions = DEFAULT_ACCEPTED_EXTENSIONS,
	fileSizeLimitMB = DEFAULT_FILE_SIZE_LIMIT_MB,
	multiple = true,
	idleLabel = 'Click to Upload',
	uploadingLabel,
	...restProps
}) => {
	const { uploads } = useUploadManagerValues();
	const { uploadFiles } = useUploadAttachmentFlow({
		entityId,
		entityType,
		description,
		acceptedExtensions,
		fileSizeLimitMB,
	});

	const extensions = useMemo(() => normalizeAcceptedExtensions(acceptedExtensions), [acceptedExtensions]);

	const inFlightCount = useMemo(() => countEntityInFlightUploads(uploads, entityId, entityType), [uploads, entityId, entityType]);

	const isUploading = inFlightCount > 0;

	const label = resolveUploadButtonLabel(inFlightCount, idleLabel, uploadingLabel);

	const handleBeforeUpload = useCallback(
		(file: RcFile, fileList: RcFile[]) => {
			if (file === fileList[0]) {
				void uploadFiles(fileList);
			}

			return Upload.LIST_IGNORE;
		},
		[uploadFiles],
	);

	const { loading, disabled, ...buttonProps } = restProps;

	return (
		<Upload
			name='file'
			multiple={multiple}
			showUploadList={false}
			accept={extensions.accept}
			beforeUpload={handleBeforeUpload}
			disabled={disabled || isUploading}
		>
			<Button {...buttonProps} loading={loading ?? isUploading} disabled={disabled || isUploading}>
				{label}
			</Button>
		</Upload>
	);
};

export default UploadAttachment;
export type { UploadAttachmentButtonProps };
